import type { UseQueryOptions } from 'react-query'
import type { CommonResponse } from 'types/api'

export type KinerjaStatus =
  | 'NOT_FOUND'
  | 'DRAFT'
  | 'WAITING_APPROVAL'
  | 'APPROVED'
  | 'REVISED'
  | 'READY'
  | 'WAITING_EVALUATION'

export type KinerjaRealisasiState =
  | 'PENDING_SUBMISSION'
  | 'WAITING_EVALUATION'
  | 'EVALUATED'
  | 'DRAFT'

export type Jenjang =
  | 'pertama'
  | 'muda'
  | 'madya'
  | 'utama'
  | 'ahli pertama'
  | 'ahli muda'
  | 'ahli madya'
  | 'ahli utama'
  | 'bawah pertama'

export type KategoriKinerja =
  | 'kinerja'
  | 'pembelajaran'
  | 'kompetensi'
  | 'tambahan'
  | 'minat'

export const listOfPangkat = [
  'LTIII/A',
  'III/A',
  'III/B',
  'III/C',
  'III/D',
  'IV/A',
  'IV/B',
  'IV/C',
  'IV/D',
  'IV/E',
] as const

export enum KinerjaPeriod {
  SEMESTER_1 = 1,
  SEMESTER_2 = 2,
}

export type Pangkat = typeof listOfPangkat[number]

export interface Kinerja {
  id: string
  tahun: number
  period: number
  status: KinerjaStatus
  pangkatGolongan: string
  jenjang: string
  kinerjaUtama: number[]
  nama?: string

  praktikPembelajaran: number
  pengembanganKompetensi: KinerjaSelection[]
  tugasTambahan: KinerjaSelection[]
  prilakuKerja: PerilakuKerjaPayload[]

  reviewPraktikPembelajaran: number
  reviewPengembanganKompetensi: KinerjaSelection[]
  reviewTugasTambahan: KinerjaSelection[]
  reviewPrilakuKerja: Changeable<PerilakuKerjaPayload>[]

  statusUnor: KonfirmasiUnorStatus
  statusPengaliran: TipePengaliranStatus
}

export interface RequestParamsKinerja {
  jenjang?: Jenjang
  ptkId?: string
}

export interface RequestParamsGetKinerja {
  ptkId: string
}

export type ResponseKinerja = CommonResponse<Kinerja>

export type KategoriPegawai = 'UNDEFINED' | 'PNS' | 'PPPK'
export type KinerjaRoleType = 'KS_PLT' | 'KS_DEFINITIF' | 'GURU'

export interface KinerjaProfilRole {
  npsn: string
  type: KinerjaRoleType
  satuanPendidikan: string
}

export interface KinerjaProfil {
  nama: string
  pangkatGolongan: string
  jenjang: string
  satuanPendidikan: string
  nip: string
  nuptk?: string
  ksAktif: boolean
  npsn: string
  statusPegawai: string
  kategoriPegawai: KategoriPegawai
  roles?: Array<KinerjaProfilRole>
}

export type ResponseKinerjaProfil = CommonResponse<KinerjaProfil>

export type RequestKinerjaProfile = {
  params?: { userId?: string; ptkId?: string }
  options?: UseQueryOptions<ResponseKinerjaProfil>
  isDemoMode?: boolean
}

export interface KinerjaDetail {
  title: string
  resultFile: string
  result: string
  targetQty: number
  targetDeadline: string
  example: string
  qualityIndicators: string[]
  profile: boolean
  grade: string
  notes: string
}

export interface BuktiDukung {
  id: number
  deskripsi: string
}

export interface KinerjaUtamaDetail {
  id: number
  order?: number
  rencanaHasilKerja: string
  buktiDukung: BuktiDukung[]
  targetKuantitas: string
  targetWaktu: number
  targetKualitas: string
  targetKuantitasMin?: number
  targetKuantitasMax?: number
  nama?: string
  definisi?: string
  bobot: number
  catatan: string
}

export interface KinerjaTambahanDetail {
  id?: number
  order?: number
  deskripsi?: string
  options: KinerjaUtamaDetail[]
}

export interface KinerjaPraktikPembelajaranContentIndikator {
  id: number
  nama: string
  definisi: string
  rencanaHasilKerja: string
  targetKualitas: string
  buktiDukung: BuktiDukung[]
  targetKuantitas: string
  targetWaktu: number
}

export interface KinerjaPraktikPembelajaranDimensi {
  dimensiId: string
  dimensiNama: string
  skor: number
  indikator: KinerjaPraktikPembelajaranContentIndikator[]
}

export interface KinerjaTugasTambahan {
  buktiDukung: BuktiDukung
  id: number
  rencanaHasilKerja: string
  targetKualitas: string
  targetKuantitas: string
  targetKuantitasMax: number
  targetKuantitasMin: number
  targetWaktu: number
}

export interface ContentKinerjaUtama {
  jenjangId: number
  jenjang: string
  kinerjaUtama: KinerjaUtamaDetail[]

  isRaporExists: boolean
  praktikPembelajaran: KinerjaPraktikPembelajaranDimensi[]
  pengembanganKompetensi: KinerjaUtamaDetail[]
  tugasTambahan: KinerjaUtamaDetail[]
}

export type ResponseContentKinerjaUtama = CommonResponse<ContentKinerjaUtama>

export interface PerilakuKerjaEkpektasi {
  id: number
  orderEkspektasi: number
  value: string
}

export interface PerilakuKerjaIndikator {
  id: number
  orderIndikator: number
  value: string
  ekspektasi: PerilakuKerjaEkpektasi[]
}

export interface PerilakuKerjaAspek {
  id: number
  orderAspek: number
  value: string
  indikator: PerilakuKerjaIndikator[]
}

export interface ContentPerilakuKerja {
  aspek: PerilakuKerjaAspek[]
}

export type ResponseContentPerilakuKerja = CommonResponse<ContentPerilakuKerja>

// "XOR" between two union types
// see: https://stackoverflow.com/questions/44425344/typescript-interface-with-xor-barstring-xor-cannumber
type Without<T, U> = { [P in Exclude<keyof T, keyof U>]?: never }
type XOR<T, U> = T | U extends object
  ? (Without<T, U> & U) | (Without<U, T> & T)
  : T | U

export type Changeable<T> = T & { isChanged?: boolean }

export interface CreateSKPPayload {
  pangkatGolongan: string
  jenjang: string
  skpYear?: number
  skpPeriod?: number
}

export interface KinerjaSelection {
  id: number
  kuantitas: number
}

export interface PerilakuKerjaPayload {
  aspekId: number
  indikatorId: number
  ekspektasiId: number
}

export interface UpdateSKPPayload {
  id: string
  // TODO: Remove these properties?
  kinerjaUtama?: number[]

  praktikPembelajaran?: number
  pengembanganKompetensi?: KinerjaSelection[]
  tugasTambahan?: KinerjaSelection[]
  prilakuKerja?: PerilakuKerjaPayload[]
  skpYear?: number
  skpPeriod?: number
}

export type UpsertSKPPayload = XOR<CreateSKPPayload, UpdateSKPPayload>

export interface GuruSKP {
  userId: string
  ptkId: string
  name: string
  pangkatGolongan: string
  jenjang: string
  skpStatus: KinerjaStatus
  skpId: string
  kategoriPegawai: KategoriPegawai
}

export type ResponseGetListGuruByNpsn = CommonResponse<GuruSKP[]>

export interface UpsertReviewSKPPayload {
  ptkId: string
  reviewPraktikPembelajaran?: number
  reviewPengembanganKompetensi?: KinerjaSelection[]
  reviewTugasTambahan?: KinerjaSelection[] | [{}]
  reviewPrilakuKerja?: Changeable<PerilakuKerjaPayload>[]
}

export interface UpdateStatePayload {
  skpId: string
}

export interface SKPTabHandler {
  saveDraft: () => void
}

export interface KinerjaPerencanaanTabBaseProps {
  skp: Kinerja
  isSubmitting?: boolean
  onClickNext?: (data: UpdateSKPPayload) => void
  onClickSave?: (data: UpdateSKPPayload) => void
}

export interface OverviewBuktiDukung {
  totalKinerja: number
  totalRealisasiKinerja: number
}

export interface OverviewRealisasi {
  period: KinerjaPeriod
  kinerjaUtama: OverviewBuktiDukung
  kinerjaTambahan: OverviewBuktiDukung
}

export interface Realisasi {
  id: string
  sequence: string
  kategoriKinerja: KategoriKinerja
  kategori: string
  deskripsi: string
  state: KinerjaRealisasiState
}

export interface RealisasiV2 {
  id: string
  deskripsi: string
  state: KinerjaRealisasiState
  bobot: number
  kategori: RealisasiTypeV2
  realisasi: number
}

interface KinerjaUpdatedDate {
  kategori: RealisasiTypeV2
  kinerjaUpdatedAt: string
}

export interface ResponseGetListRealisasiV2 {
  data: RealisasiV2[]
  bobotTotal: number
  kinerjaUpdated: KinerjaUpdatedDate[]
  realisasiTotal: number
  realisasiResult: RealisasiResultType
}

export type RealisasiResultType =
  | 'MEET_EXPECTATION'
  | 'EXCEED_EXPECTATION'
  | 'BELOW_EXPECTATION'

/**
 * Represents the type of realisasi.
 * Possible values are:
 * - 'KU': Kinerja Utama
 * - 'PP': Praktik Pembelajaran
 * - 'PK': Pengembangan Kompetensi
 * - 'TT': Tugas Tambahan
 */
export type RealisasiTypeV2 = 'KU' | 'PP' | 'PK' | 'TT'

export type ResponseGetOverviewRealisasi = CommonResponse<OverviewRealisasi[]>

export type ResponseGetListRealisasi = CommonResponse<Realisasi[]>

export interface BuktiDukungFileInstance {
  id: string
  deskripsi: string
  fileUrl: string
  fileName: string
}

export interface BuktiDukungRealisasi {
  sequence: string
  deskripsi: string
  targetKualitas: string
  state: KinerjaRealisasiState
  catatan: string
  bobot: number
  buktiDukung: BuktiDukungFileInstance[]
}

export interface RequestParamsRealisasi {
  realisasiId: string
  periodParams?: MultiPeriodParams
  userId?: string
}

export type ResponseBuktiDukung = CommonResponse<BuktiDukungRealisasi>

export enum SourceUploadDialogPage {
  HOME,
  BUKTI_KARYA_CATEGORIES,
  BUKTI_KARYA_DOCUMENT_LIST,
}

export interface SourceUploadDialogPageBaseProps<P> {
  onSetPage: (page: SourceUploadDialogPage, params?: P) => void
}

export interface BuktiKaryaCategory {
  id?: number
  name: string
  total: number
  key: string
  iconName: string
}

export interface TotalBuktiKaryaDocument {
  categoryId: string
  total: number
}

export type ResponseTotalBuktiKaryaDocument = CommonResponse<
  Array<TotalBuktiKaryaDocument>
>

export interface RequestUploadFromBuktiKarya {
  realisasiId: string
  state: string

  buktiDukung: Array<{
    id: string
    deskripsi: string
    fileUrl: string
    fileName: string
    buktiKaryaId: string
  }>
}

export interface SKPPeriod {
  period: number
  date: string
  periodeKinerja: PeriodeKinerja[]
}

export interface SKPPeriodV2 extends Omit<SKPPeriod, 'date'> {
  id: string
  startDate: string
  endDate: string
  isApproved: boolean
}

export type ResponseSKPPeriodV2 = CommonResponse<SKPPeriodV2[]>

export interface RequestParamsApproveSKPPeriod {
  periode_id: string
  is_approved: boolean
  npsn: string
}

export interface PeriodeKinerja {
  key: string
  value: string
}

export type KonfirmasiUnorStatus =
  | 'NOT_SUBMITTED'
  | 'UNOR_MATCH'
  | 'UNOR_UNMATCH'
  | 'UNOR_UNMATCH_SUPERIOR'
  | 'UNOR_UNMATCH_ORG'

export type TipePengaliranStatus = 'NOT_DELIVERED' | 'DELIVERED' | 'FAILED'

export interface UnorPegawai {
  nama: string
  unorIdPegawai: string
  satuanPendidikan: string
  namaAtasan: string
  unorIdAtasan: string
  nipAtasan: string
  statusAtasan: string
}

export interface MultiPeriodPayload {
  skpPeriod: number
  skpYear: number
}

export type CommonMultiPeriodMeta = { skpYear: number; skpPeriod: number }

export interface MultiPeriodParams {
  skp_period: number
  skp_year: number
}

export interface RequestGetReviewSKP {
  ptkId: string
  skp_year?: number
  skp_period?: number
}
